import React, { useState } from "react";
import { Button, Typography } from "@mui/material";
import { setDialog } from "../../../../features/dialog/dialogSlice";
import { useDispatch } from "react-redux";

import {
  useGetBankingAccountsQuery,
  useCreateBankingAccountMutation,
  useUpdateBankingAccountMutation,
  useMarkAccountAsMainMutation,
  useDeleteBankingAccountMutation,
} from "../../../../features/banking/bankingApiSlice";

import AccountsTable from "./AccountsTable";
import AccountForm from "./AccountForm";

export default function BankingSettings() {
  const dispatch = useDispatch();

  const {
    data: accounts = [],
    isLoading,
    isError,
    error,
  } = useGetBankingAccountsQuery();

  const [createAccount] = useCreateBankingAccountMutation();
  const [updateAccount] = useUpdateBankingAccountMutation();
  const [markAccountAsMain] = useMarkAccountAsMainMutation();
  const [deleteBankingAccount] = useDeleteBankingAccountMutation();

  const [showForm, setShowForm] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);

  // Called when user clicks "Edit"
  const handleEditAccount = (account) => {
    setSelectedAccount(account);
    setShowForm(true);
  };

  // Called when user clicks "Neuer Account"
  const handleAddNew = () => {
    setSelectedAccount(null);
    setShowForm(true);
  };

  // Called when user marks account as main
  const handleMarkMain = async (accountId) => {
    try {
      const response = await markAccountAsMain(accountId).unwrap();
      dispatch(
        setDialog({
          title: "Hauptkonto gesetzt",
          type: "SUCCESS",
          data: { successText: response.message },
        })
      );
    } catch (err) {
      console.error("Error marking account as main:", err);
      dispatch(
        setDialog({
          title: "Fehler",
          type: "ERROR",
          data: {
            errorText: "Konto konnte nicht als Hauptkonto gesetzt werden.",
          },
        })
      );
    }
  };

  // NEW: Called when user clicks "Löschen"
  const handleDeleteAccount = async (accountId) => {
    const confirm = window.confirm(
      "Möchten Sie dieses Konto wirklich löschen?"
    );
    if (!confirm) return;

    try {
      const response = await deleteBankingAccount({ id: accountId }).unwrap();
      if (response?.status === "SUCCESS") {
        dispatch(
          setDialog({
            title: "Konto gelöscht",
            type: "SUCCESS",
            data: { successText: response.message },
          })
        );
      }
    } catch (err) {
      console.error("Error deleting account:", err);
      dispatch(
        setDialog({
          title: "Fehler beim Löschen",
          type: "ERROR",
          data: {
            errorText: "Das Konto konnte nicht gelöscht werden.",
          },
        })
      );
    }
  };

  // Called when form is submitted (create or update)
  const handleSaveAccount = async (data) => {
    try {
      if (selectedAccount?._id) {
        const updatedData = { ...selectedAccount, ...data };
        const result = await updateAccount(updatedData).unwrap();
        if (result?.status === "SUCCESS") {
          dispatch(
            setDialog({
              title: "Konto aktualisiert",
              type: "SUCCESS",
              data: { successText: result.message },
            })
          );
        }
      } else {
        const result = await createAccount(data).unwrap();
        if (result?.status === "SUCCESS" || result?.message) {
          dispatch(
            setDialog({
              title: "Konto erstellt",
              type: "SUCCESS",
              data: { successText: result.message },
            })
          );
        }
      }
      setShowForm(false);
      setSelectedAccount(null);
    } catch (err) {
      console.error("Error saving account:", err);
      dispatch(
        setDialog({
          title: "Fehler beim Speichern",
          type: "ERROR",
          data: {
            errorText: "Es gab ein Problem. Bitte versuchen Sie es erneut.",
          },
        })
      );
    }
  };

  if (isLoading) return <p>Lade Bankdaten ...</p>;
  if (isError) {
    console.error(error);
    return <p>Fehler beim Laden der Bankdaten.</p>;
  }

  // If no accounts => show form for first account
  if (accounts.length === 0 && !showForm) {
    return (
      <div>
        <Typography variant="h5" sx={{ mb: 2 }}>
          Noch keine Bankkonten vorhanden
        </Typography>
        <AccountForm
          existingAccount={null}
          onSave={handleSaveAccount}
          onCancel={() => {}}
        />
      </div>
    );
  }

  return (
    <div>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Ihre gespeicherten Bankkonten
      </Typography>

      {/* Table of existing accounts */}
      <AccountsTable
        accounts={accounts}
        onEdit={handleEditAccount}
        onMarkMain={handleMarkMain}
        onDelete={handleDeleteAccount}
      />

      {/* "Neuer Account" button => set selectedAccount=null, show form */}
      {!showForm && (
        <Button variant="contained" sx={{ mt: 2 }} onClick={handleAddNew}>
          Neuer Account
        </Button>
      )}

      {showForm && (
        <AccountForm
          existingAccount={selectedAccount}
          onSave={handleSaveAccount}
          onCancel={() => {
            setShowForm(false);
            setSelectedAccount(null);
          }}
        />
      )}
    </div>
  );
}
